import { getRenderModel } from "./svg-source";
import type { BuilderConfig, RenderPath } from "./types";

function pathMarkup(p: RenderPath): string {
  const attrs = [`d="${p.d}"`, `fill="${p.fill}"`];
  if (p.stroke) attrs.push(`stroke="${p.stroke}"`);
  if (p.strokeWidth !== undefined) attrs.push(`stroke-width="${p.strokeWidth}"`);
  if (p.strokeMiterlimit !== undefined) attrs.push(`stroke-miterlimit="${p.strokeMiterlimit}"`);
  return `<path ${attrs.join(" ")}/>`;
}

export function buildLogoSvgString(config: BuilderConfig): string {
  const { geometry, background, paths } = getRenderModel(config);
  const { viewBox, outputW, outputH } = geometry;
  const lines = [
    `<svg width="${outputW}" height="${outputH}" viewBox="${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}" fill="none" xmlns="http://www.w3.org/2000/svg">`,
  ];
  if (background) {
    const radius =
      background.radius > 0 ? ` rx="${background.radius}" ry="${background.radius}"` : "";
    lines.push(
      `<rect x="${viewBox.x}" y="${viewBox.y}" width="${viewBox.w}" height="${viewBox.h}"${radius} fill="${background.fill}"/>`
    );
  }
  for (const p of paths) lines.push(pathMarkup(p));
  lines.push("</svg>");
  return lines.join("\n");
}

export function buildFilename(config: BuilderConfig, ext: "svg" | "png"): string {
  const parts = ["proxidize", config.asset, config.color];
  if (config.asset === "wordmark" && !config.registered) parts.push("no-r");
  if (config.asset === "wordmark" && config.canvas === "square") parts.push("square");
  parts.push(config.background === "none" ? "transparent" : `on-${config.background}`);
  if (config.background !== "none" && config.cornerRadius > 0) {
    parts.push(`r${config.cornerRadius}`);
  }
  if (config.padding) parts.push("clearspace");
  parts.push(
    config.asset === "wordmark" && config.canvas === "custom"
      ? `${config.customWidth}x${config.customHeight}`
      : String(config.size)
  );
  return `${parts.join("-")}.${ext}`;
}

function triggerDownload(url: string, filename: string): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadSvg(config: BuilderConfig): void {
  const blob = new Blob([buildLogoSvgString(config)], { type: "image/svg+xml;charset=utf-8" });
  triggerDownload(URL.createObjectURL(blob), buildFilename(config, "svg"));
}

export async function downloadPng(config: BuilderConfig): Promise<void> {
  const { geometry } = getRenderModel(config);
  const svg = buildLogoSvgString(config);

  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("Could not rasterize the SVG"));
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });

  const canvas = document.createElement("canvas");
  canvas.width = geometry.outputW;
  canvas.height = geometry.outputH;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas 2D context unavailable");
  context.drawImage(image, 0, 0, geometry.outputW, geometry.outputH);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("PNG encoding failed");
  triggerDownload(URL.createObjectURL(blob), buildFilename(config, "png"));
}
